import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, MapPin, Users, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";

function fmtDetail(d) {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
}

export default function Login() {
  const { setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Please fill both fields"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      setUser(r.data.user);
      await refresh();
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      setError(fmtDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-50">
      {/* Visual side */}
      <aside className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-700 text-white p-12 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-amber-300/20 blur-3xl"></div>
        <div className="relative z-10 flex flex-col justify-between w-full">
          <Link to="/" className="inline-flex items-center bg-white/95 rounded-2xl px-4 py-3 shadow-xl self-start">
            <img src="/brand/mylivefriend-logo.png" alt="MyLiveFriend" className="h-12 w-auto" draggable="false" />
          </Link>
          <div>
            <h2 className="font-heading text-4xl xl:text-5xl font-extrabold leading-[1.1]">
              Find the right <br />friend for you.
            </h2>
            <p className="mt-4 text-white/80 text-lg max-w-md leading-relaxed">
              Connect with people, make new friends and discover meaningful experiences — all in your city, your pincode, your pace.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {[
                { Icon: Users, k: "480+", v: "Neighbours" },
                { Icon: MapPin, k: "10", v: "Cities" },
                { Icon: ShieldCheck, k: "100%", v: "Verified" },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl bg-white/10 backdrop-blur border border-white/15 p-4">
                  <s.Icon className="w-5 h-5 opacity-90" />
                  <div className="font-heading text-2xl font-extrabold mt-2">{s.k}</div>
                  <div className="text-xs opacity-80 uppercase tracking-widest">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-white/60">© 2026 MyLiveFriend. Private & secure.</div>
        </div>
      </aside>

      {/* Form side */}
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6">
            <Logo size="lg" />
          </div>

          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Welcome back</h1>
          <p className="text-slate-500 mt-1">Sign in to continue to your dashboard.</p>

          <form onSubmit={handleLogin} className="mt-7 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="login-email" id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" autoComplete="email" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-teal-700 hover:text-teal-800">Forgot password?</Link>
              </div>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="login-password" id="password" type={showPass ? "text" : "password"} placeholder="Minimum 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 pr-10" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox id="remember" checked={remember} onCheckedChange={setRemember} data-testid="remember-checkbox" />
              <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">Remember me</Label>
            </div>

            {error && (<div data-testid="login-error" className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">{error}</div>)}

            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-semibold">
              {loading ? "Signing in..." : "Login with Email"}
            </Button>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
              <div className="relative flex justify-center"><span className="bg-slate-50 lg:bg-white px-3 text-xs uppercase font-semibold tracking-widest text-slate-400">or</span></div>
            </div>

            <Button data-testid="login-google" type="button" onClick={handleGoogle} variant="outline" className="w-full h-11 rounded-full font-semibold border-slate-300 hover:bg-slate-50">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.3 29.4 35.5 24 35.5A11.5 11.5 0 1 1 24 12.5c3 0 5.6 1.1 7.6 2.9l5.7-5.7A19.4 19.4 0 0 0 24 4a20 20 0 1 0 20 20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.1l6.6 4.8A11.5 11.5 0 0 1 24 12.5c3 0 5.6 1.1 7.6 2.9l5.7-5.7A19.4 19.4 0 0 0 24 4 20 20 0 0 0 6.3 14.1z"/><path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3c-2 1.4-4.5 2.1-7.4 2.1-5.4 0-9.9-3.1-11.4-7.4l-6.5 5a20 20 0 0 0 18 10.9z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.3 5.3c6.7-4.8 6.7-15 0-1.2-.1-2.3-.4-3.5z"/></svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-slate-600 mt-6">
              Don't have an account? <Link to="/signup" data-testid="signup-link" className="font-semibold text-teal-700 hover:text-teal-800">Sign up</Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
